import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { getInsuranceQuote } from "../../store/actions/shipment.action";
import "./ShipmentProtection.css";

/** Display names for the providers the quote API can return. */
const PROVIDER_NAMES = {
  UPIC: "U-PIC",
  "U-PIC": "U-PIC",
  SHIPSURANCE: "Shipsurance",
};

/**
 * Ids CreateUpdateShipment expects in insuranceDetails. GetQuote identifies the
 * provider by name only, so map it here until the ids come back on the quote.
 */
const PROVIDER_IDS = { UPIC: 1, "U-PIC": 1 };
const DEFAULT_PROVIDER_ID = 1;
const DEFAULT_SERVICE_ID = 1;

const QUOTE_DEBOUNCE = 800;
const QUOTE_FAILED = "Unable to fetch an insurance quote.";

const round2 = (num) => Math.round((Number(num) || 0) * 100) / 100;

const MAX_VALUE_DIGITS = 10;
const MAX_VALUE_DECIMALS = 2;

/** Keeps the declared value to digits with a single point and two decimals. */
const sanitizeDeclaredValue = (input) => {
  const cleaned = String(input ?? "").replaceAll(/[^\d.]/g, "");
  const dot = cleaned.indexOf(".");
  if (dot < 0) return cleaned.slice(0, MAX_VALUE_DIGITS);
  return [
    cleaned.slice(0, dot).slice(0, MAX_VALUE_DIGITS),
    cleaned
      .slice(dot + 1)
      .replaceAll(".", "")
      .slice(0, MAX_VALUE_DECIMALS),
  ].join(".");
};

/** "100." is only ever a half typed value, so the point goes on blur. */
const trimTrailingDot = (input) => String(input ?? "").replace(/\.$/, "");

const money = (num) =>
  `$${(Number(num) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** GetQuote and a saved order both hand back the same quote entity. */
const mapQuoteEntity = (entity) => ({
  quoteUuid: entity.quote_uuid,
  providerName:
    PROVIDER_NAMES[entity.insurance_provider] || entity.insurance_provider || "",
  providerId:
    entity.insuranceprovider_id ||
    PROVIDER_IDS[entity.insurance_provider] ||
    DEFAULT_PROVIDER_ID,
  serviceId: entity.insuranceservice_id || DEFAULT_SERVICE_ID,
  currency: entity.currency,
  status: entity.status,
  premium: round2(entity.premium_amount),
});

/**
 * The quote an order was last saved with, for pages that load an order and want
 * its priced premium on screen before any new quote goes out.
 */
export const savedInsuranceQuote = (entity) => {
  if (!entity || entity.error || entity.errcode) return null;
  if (!(Number(entity.premium_amount) > 0)) return null;
  return mapQuoteEntity(entity);
};

/** Turns a GetQuote response into either a quote or an error message. */
const parseQuoteResponse = (response) => {
  const entity = response?.data?.Entity ?? response?.data;
  if (!entity || entity.error || entity.errcode) {
    return { error: entity?.error || QUOTE_FAILED };
  }
  return { quote: mapQuoteEntity(entity) };
};

/**
 * The GetQuote payload, built from the signed in user and the order being
 * priced. Every page that quotes sends the same fields.
 */
export const buildInsuranceQuoteContext = (user = {}, order = {}) => ({
  pageIndex: 1,
  pageSize: 10,
  sortColumn: "",
  sortOrder: "ASCENDING",
  searchkey: "",
  employeeId: user.employeeId || 0,
  companyId: user.companyId || 0,
  sellerId: user.sellerId || 0,
  createdBy: user.employeeId || 0,
  createdDate: new Date().toISOString(),
  isActive: true,
  modifiedBy: 0,
  modifiedDate: new Date().toISOString(),
  companySellerId: user.companySellerId || 0,
  role: user.role || 0,
  isSuperAdmin: !!user.isSuperAdmin,
  tenantId: user.tenantId || 1,
  isBillingCycle: true,
  userEmail: user.userEmailId || user.emailId || user.email || "",
  fromCountryCode: order.fCountry || "",
  fromZip: order.fPostcode || "",
  toCountryCode: order.sCountry || "",
  toZip: order.sPostcode || "",
  currency: "USD",
  carrier: order.carrierCompanyName || "",
  trackingCode: "",
  companyName: order.fCompany || "",
  email: order.fEmail || "",
  orderNumber: order.ordernumber || "",
});

/** Never rejects: a failed request comes back as an error message. */
const requestQuote = async (dispatch, payload) => {
  try {
    const response = await Promise.resolve(
      dispatch(getInsuranceQuote(payload))
    );
    return parseQuoteResponse(response);
  } catch (error) {
    return { error: error?.message || QUOTE_FAILED };
  }
};

/**
 * Keeps a quote in step with the declared value. Like the zip zone rate call, it
 * only goes out once the shipment has no outstanding validation errors. Only the
 * fields that can change the premium trigger a re-quote, so order level edits
 * (duplicating a shipment clears the order number) keep the quote as is.
 */
const useInsuranceQuote = ({
  canQuote,
  initialQuote,
  isProtected,
  quoteContext,
  value,
}) => {
  const dispatch = useDispatch();
  const [quote, setQuote] = useState(initialQuote || null);
  const [quoteLoader, setQuoteLoader] = useState(false);
  const [quoteError, setQuoteError] = useState("");

  const quoteSignature = [
    quoteContext.fromCountryCode,
    quoteContext.fromZip,
    quoteContext.toCountryCode,
    quoteContext.toZip,
    quoteContext.carrier,
    value,
  ].join("|");

  useEffect(() => {
    if (!isProtected || value <= 0) {
      setQuote(null);
      setQuoteError("");
      setQuoteLoader(false);
      return undefined;
    }

    // Hold, but keep any quote already on screen, until the form is valid.
    if (!canQuote) return undefined;

    // Guards against a slow response landing after a newer request started.
    let cancelled = false;
    const timer = setTimeout(async () => {
      setQuoteLoader(true);
      setQuoteError("");
      const result = await requestQuote(dispatch, {
        ...quoteContext,
        value,
        insuredValue: value,
      });
      if (cancelled) return;
      setQuote(result.quote || null);
      setQuoteError(result.error || "");
      setQuoteLoader(false);
    }, QUOTE_DEBOUNCE);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canQuote, isProtected, quoteSignature]);

  return { quote, quoteLoader, quoteError };
};

const QUOTE_LOADING = "Fetching the latest rate...";

const declaredValueHint = (isValueMissing, quoteError, quoteLoader) => {
  if (isValueMissing) return "Declared value is required.";
  if (quoteLoader) return QUOTE_LOADING;
  if (quoteError) return quoteError;
  return "";
};

/** Only rendered once a quote has come back, so every figure here is real. */
const ProviderRow = ({ quote }) => (
  <>
    <div className="short-txt">Provider</div>
    <div className="shipProtectProviders">
      <span className="shipProtectChip isSelected">
        <b>{quote.providerName}</b>
        <span className="amount">{money(quote.premium)}</span>
      </span>
    </div>
  </>
);

ProviderRow.propTypes = {
  quote: PropTypes.shape({
    providerName: PropTypes.string,
    premium: PropTypes.number,
  }),
};

/** Only rendered once a quote has come back, so every figure here is real. */
const ProtectionSummary = ({ quote, value }) => (
  <div className="col-sm-12 col-lg-5">
    <div className="shipProtectSummary">
      <div className="shipProtectRow">
        <span>Premium</span>
        <b>{money(quote.premium)}</b>
      </div>
      <div className="shipProtectRow isTotal">
        <span>You pay</span>
        <b>{money(quote.premium)}</b>
      </div>
      <p className="shipProtectNote">
        Covers up to {money(value)} &middot; {quote.providerName}.
      </p>
    </div>
  </div>
);

ProtectionSummary.propTypes = {
  quote: PropTypes.shape({
    providerName: PropTypes.string,
    premium: PropTypes.number,
  }),
  value: PropTypes.number,
};

const ProtectionBody = ({
  declaredValue,
  hasQuote,
  isValueMissing,
  onDeclaredValueBlur,
  onDeclaredValueChange,
  quote,
  quoteError,
  quoteLoader,
  value,
}) => {
  const hasFieldError = isValueMissing || (!!quoteError && !quoteLoader);
  return (
    <div className={`shipProtectBody ${quoteLoader ? "isQuoting" : ""}`}>
      <div className="row">
        <div className="col-sm-12 col-lg-7">
          {hasQuote && <ProviderRow quote={quote} />}

          <div className={`short-txt ${hasQuote ? "mt-2" : ""}`}>
            Declared Value
          </div>
          <div
            className={`shipProtectAmount ${hasFieldError ? "errorField" : ""}`}
          >
            <span>$</span>
            <input
              type="text"
              inputMode="decimal"
              maxLength={MAX_VALUE_DIGITS + MAX_VALUE_DECIMALS + 1}
              className="insuranceInput form-control"
              value={declaredValue}
              onFocus={(e) => e.target.select()}
              onChange={onDeclaredValueChange}
              onBlur={onDeclaredValueBlur}
            />
          </div>
          <div className={`shipProtectHint ${hasFieldError ? "isError" : ""}`}>
            {declaredValueHint(isValueMissing, quoteError, quoteLoader)}
          </div>
        </div>

        {hasQuote && <ProtectionSummary quote={quote} value={value} />}
      </div>
    </div>
  );
};

ProtectionBody.propTypes = {
  declaredValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasQuote: PropTypes.bool,
  isValueMissing: PropTypes.bool,
  onDeclaredValueBlur: PropTypes.func,
  onDeclaredValueChange: PropTypes.func,
  quote: PropTypes.object,
  quoteError: PropTypes.string,
  quoteLoader: PropTypes.bool,
  value: PropTypes.number,
};

const ShipmentProtection = ({
  canQuote = true,
  compact = false,
  initialQuote = null,
  quoteContext = {},
  selection,
  onSelectionChange,
  onChange,
}) => {
  // The toggle and declared value live on the page, so they survive a shipment
  // being booked and carry over to a duplicate shipment.
  const isProtected = !!selection?.isProtected;
  const declaredValue = selection?.declaredValue ?? "";
  const updateSelection = (patch) => {
    if (typeof onSelectionChange !== "function") return;
    onSelectionChange({ isProtected, declaredValue, ...patch });
  };

  const value = Number(declaredValue) || 0;
  const isValueMissing = isProtected && value <= 0;

  const { quote, quoteError, quoteLoader } = useInsuranceQuote({
    canQuote,
    initialQuote,
    isProtected,
    quoteContext,
    value,
  });

  const hasQuote = !!(isProtected && quote && !quoteError);

  useEffect(() => {
    if (typeof onChange !== "function") return;
    onChange({
      isProtected,
      quoteUuid: quote?.quoteUuid || "",
      providerName: quote?.providerName || "",
      providerId: quote?.providerId || DEFAULT_PROVIDER_ID,
      serviceId: quote?.serviceId || DEFAULT_SERVICE_ID,
      declaredValue: value,
      premium: hasQuote ? quote.premium : 0,
      payable: hasQuote ? quote.premium : 0,
      hasError: isProtected && !hasQuote,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProtected, quote, quoteError, value]);

  // Drop the protection charges from the rate box when the panel goes away.
  useEffect(() => {
    return () => {
      if (typeof onChange === "function") onChange(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`shipProtect ${isProtected ? "isOn" : ""} ${
        compact ? "isCompact" : ""
      }`}
    >
      <div className="shipProtectHead">
        <i className="fa fa-shield shipProtectIcon" aria-hidden="true"></i>
        <div className="shipProtectTitle">
          <b>Shipment Protection</b>
          <span>Protect against loss, damage, or theft in transit.</span>
        </div>
        {quoteLoader && (
          <div className="shipProtectCost isQuoting">
            <Spinner
              animation="border"
              size="sm"
              role="status"
              aria-label={QUOTE_LOADING}
            />
          </div>
        )}
        {!quoteLoader && hasQuote && (
          <div className="shipProtectCost">
            <span>You pay</span>
            <b>{money(quote.premium)}</b>
          </div>
        )}
        <label className="shipProtectSwitch">
          <input
            type="checkbox"
            id="shipmentProtection"
            checked={isProtected}
            onChange={(e) => updateSelection({ isProtected: e.target.checked })}
            aria-label="Add shipment protection"
          />
          <i></i>
        </label>
      </div>

      {isProtected && (
        <ProtectionBody
          declaredValue={declaredValue}
          hasQuote={hasQuote}
          isValueMissing={isValueMissing}
          onDeclaredValueChange={(e) =>
            updateSelection({
              declaredValue: sanitizeDeclaredValue(e.target.value),
            })
          }
          onDeclaredValueBlur={() =>
            updateSelection({ declaredValue: trimTrailingDot(declaredValue) })
          }
          quote={quote}
          quoteError={quoteError}
          quoteLoader={quoteLoader}
          value={value}
        />
      )}
    </div>
  );
};

ShipmentProtection.propTypes = {
  canQuote: PropTypes.bool,
  // Stacks the body instead of splitting it in two, for narrow side panels.
  compact: PropTypes.bool,
  // The premium a loaded order already carries, shown until the quote lands.
  initialQuote: PropTypes.object,
  quoteContext: PropTypes.object,
  selection: PropTypes.shape({
    isProtected: PropTypes.bool,
    declaredValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onSelectionChange: PropTypes.func,
  onChange: PropTypes.func,
};

export default ShipmentProtection;
